import { useCallback, useEffect, useRef, useState } from "react";

import { logger } from "util/logger";

import { toast } from "react-toastify";
import Video, {
  ConnectOptions,
  RemoteAudioTrack,
  RemoteVideoTrack,
  Room,
  LocalTrack,
} from "twilio-video";

// @ts-ignore
window.TwilioVideo = Video;

const mediaErrors = [
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
  'OverconstrainedError',
  'TypeError'
];

export const useRoom = (options?: ConnectOptions) => {
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasConnected, setHasConnected] = useState(false);
  const [isPatientVideoOff, setIsPatientVideoOff] = useState(false);
  const [remoteVideoTrack, setRemoteVideoTrack] =
    useState<RemoteVideoTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] =
    useState<RemoteAudioTrack | null>(null);
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const isManualDisconnect = useRef(false);

  const connect = useCallback((token: string, roomName: string, localTracks: LocalTrack[]) => {
    return Video.connect(token, {
      name: roomName,
      tracks: localTracks,
    }).then(
      (newRoom) => {
        setRoom(newRoom);

        isManualDisconnect.current = false;

        const disconnect = () => {
          isManualDisconnect.current = true;
          newRoom.disconnect();
        };
        newRoom.setMaxListeners(16);

        newRoom.once("disconnected", (error) => {
          setTimeout(() => setRoom(null));
          window.removeEventListener("beforeunload", disconnect);
          if (error && !isManualDisconnect.current) {
            toast.error("接続が切れました。環境の良いところで再度入室してください。");
          }
        });

        // 参加者が退出したことを検知
        newRoom.on('participantDisconnected', _ => {
          if(newRoom.participants.size === 0){
            setHasConnected(false)
          }
        });

        // @ts-ignore
        window.twilioRoom = newRoom;

        newRoom.localParticipant.videoTracks.forEach((publication) =>
          publication.setPriority("low")
        );

        // Add a listener to disconnect from the room when a user closes their browser
        window.addEventListener("beforeunload", disconnect);

        newRoom.on("trackDisabled", (track) => {
          if(newRoom.participants.size > 0){
            if(track.kind === "video") {
              setIsPatientVideoOff(true)
            } else if(track.kind === "audio") {
              console.log("###audio", track.kind)
            }
            
          }
        })

        newRoom.on("trackEnabled", (track) => {
          if(newRoom.participants.size > 0){
            if(newRoom.participants.size > 0){
              if(track.kind === "video") {
                setIsPatientVideoOff(false)
              }
            }
          }
        })

        // Log new Participants as they connect to the Room
        newRoom.on("participantConnected", (participant) => {
          setIsConnecting(true);
          setHasConnected(true)
          participant.on("trackSubscribed", (track) => {
            if (track.kind === "video") setRemoteVideoTrack(track);
            if (track.kind === "audio") setRemoteAudioTrack(track);
          });
        });

        // Log any Participants already connected to the Room
        newRoom.participants.forEach((participant) => {
          setIsConnecting(true);
          setHasConnected(true);

          // 既存参加者の既にpublishされているトラックを明示的に取得
          participant.tracks.forEach((publication) => {
            if (publication.isSubscribed && publication.track) {
              const track = publication.track;
              if (track.kind === "video") setRemoteVideoTrack(track);
              if (track.kind === "audio") setRemoteAudioTrack(track);
            }
          });

          // 将来のトラックのためにイベントリスナーも設定
          participant.on("trackSubscribed", (track) => {
            if (track.kind === "video") setRemoteVideoTrack(track);
            if (track.kind === "audio") setRemoteAudioTrack(track);
          });
        });
      },
      (error) => {
        toast.error(error);
        setIsConnecting(false);
        setHasConnected(false)
      }
    ).catch(error => {
      logger({
        log: "Twilio接続エラー",
        errorType: "media",
        errorMessage: error instanceof Error ? error.message : '不明なエラー'
      });
      
      if (typeof error === 'object' && error !== null && 'name' in error) {
        const errorName = String(error.name);
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';

        if (mediaErrors.includes(errorName)) {
          switch (errorName) {
            case "NotFoundError":
              toast.error("システムによって許可が拒否されました");
              logger({
                log: "NotFoundError",
                errorType: "media",
                errorMessage
              });
              break;
            case "NotAllowedError":
              toast.error("アクセス許可が拒否されました");
              logger({
                log: "NotAllowedError",
                errorType: "media",
                errorMessage
              });
              break;
            case "NotReadableError":
              toast.error("メディアデバイスにアクセスできません");
              logger({
                log: "NotReadableError",
                errorType: "media",
                errorMessage
              });
              break;
            case "OverconstrainedError":
              toast.error("メディア制約エラーが発生しました");
              logger({
                log: "OverconstrainedError",
                errorType: "media",
                errorMessage
              });
              break; 
            default:
              toast.error(`メディア接続エラー: ${errorName}`);
              logger({
                log: "メディア接続エラー",
                errorType: "media",
                errorMessage
              });
              break;
          }
        } else if ('code' in error) {
          const twilioErrorCode = error.code;
          toast.error(`接続エラー: ${twilioErrorCode}`);
          logger({
            log: "Twilioルーム接続エラー",
            errorType: "media",
            errorMessage
          });
        } else {
          toast.error("予期せぬエラーが発生しました");
          logger({
            log: "不明なTwilioエラー",
            errorType: "media",
            errorMessage
          });
        }
      } else {
        toast.error("予期せぬエラーが発生しました");
        logger({
          log: "不明なTwilioエラー",
          errorType: "media",
          errorMessage: "詳細不明"
        });
      }
    });
  }, []);

  return { room, isConnecting, hasConnected, connect, remoteVideoTrack, remoteAudioTrack, isPatientVideoOff };
};
